<template>
  <div class="wrapper" v-if="shouldRender">
    <div v-for="(tag, index) in tags" :key="`${tag.name}-${index}`" class="tag-container">
      <div class="tag-header">
        <button @click="() => removeTag(index)" class="remove-btn">
          <CloseIcon />
        </button>

        <input
          type="text"
          class="tag-name-input"
          :class="{ error: tagNames.filter(name => name.trim() === tag.name).length > 1 }"
          :value="tag.name"
          @change="event => handleTagNameChange(event, index)"
          :style="{ '--max-length': `${tag.name.length}ch` }"
        />

        <button
          v-if="!!availableGroups.length"
          @click="() => oepnGroupDrawer(index)"
          class="add-group space-hz"
        >
          <div class="plus">+</div>

          <div v-if="groupDrawerOpenIndex === index" class="group-options">
            <button
              v-for="groupId in availableGroups"
              :key="`add-${groupId}`"
              @click="event => addGroup(groupId, index, event)"
            >
              {{ groupMapping[groupId].group_name }}
            </button>
          </div>
        </button>

        <div class="numeric-tag-wrapper">
          <span>{{ parseTranslation(CHOOSE) }}</span>

          <input
            class="tag-input"
            type="number"
            min="1"
            :max="survey.questions_groups.length"
            step="1"
            :value="tag.maxGroupsToShow"
            @change="event => handleGroupsToShowChange(event, index)"
          />

          <span>{{ parseTranslation(GROUPS_TO_APPEAR) }}</span>
        </div>
      </div>

      <div v-if="tag.groups.length" class="tag-groups">
        <div v-for="group in tag.groups" :key="group">
          <div class="single-group-line">
            <span>{{ groupMapping[group].group_name }}</span>

            <button @click="() => removeGroup(group, index)" class="remove-btn space-hz">
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canAddTag" class="add-container">
      <button @click="addTag">
        <div class="plus">+</div>
        <span>{{ parseTranslation(ADD_TAG) }}</span>
      </button>
    </div>

    <div
      v-if="groupDrawerOpenIndex !== null"
      class="group-add-background"
      @click="closeGroupDrawer"
    ></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import CloseIcon from '@/components/imgs/Close'

import * as translationScheme from './index.translations'

const { default: translations, ...translationKeys } = translationScheme

export default {
  data() {
    return {
      translations,
      ...translationKeys,
      option: 'randomize_by_tags',
      groupDrawerOpenIndex: null,
    }
  },

  components: {
    CloseIcon,
  },

  computed: {
    ...mapGetters(['survey']),

    value() {
      return this.survey.advancedOptions?.[this.option]
    },

    shouldRender() {
      return Array.isArray(this.survey.advancedOptions?.[this.option])
    },

    tags() {
      return this.survey.advancedOptions[this.option]
    },

    tagNames() {
      return this.tags.map(({ name }) => name)
    },

    groupMapping() {
      return this.survey.questions_groups.reduce(
        (acc, nextGroup) => ({
          ...acc,
          [nextGroup.group_code]: nextGroup,
        }),
        {},
      )
    },

    availableGroups() {
      const allGroupIds = this.survey.questions_groups.map(({ group_code }) => group_code)
      const chosenGroups = this.tags.map(({ groups }) => groups).flat()

      return allGroupIds.filter(id => !chosenGroups.includes(id))
    },

    canAddTag() {
      if (!this.shouldRender) return false

      const numberOfGroups = this.survey.questions_groups.length

      if (this.tags.length >= numberOfGroups) return false

      const groupsWithTags = this.tags.reduce(
        (acc, nextTag) => acc + nextTag.groups.length,
        0,
      )

      return groupsWithTags < numberOfGroups
    },
  },

  watch: {
    value() {
      if (typeof this.value === 'boolean' && this.value) {
        this.changeSurveyAdvancedOptions({
          advancedKey: this.option,
          value: [],
        })
      }
    },
  },

  methods: {
    ...mapActions(['changeSurveyAdvancedOptions']),

    closeGroupDrawer() {
      this.groupDrawerOpenIndex = null
    },

    oepnGroupDrawer(index) {
      if (!this.availableGroups.length) return

      this.groupDrawerOpenIndex = index
    },

    addTag() {
      this.changeSurveyAdvancedOptions({
        advancedKey: this.option,
        value: [
          ...this.tags,
          { name: `Tag #${this.tags.length + 1}`, groups: [], maxGroupsToShow: 1 },
        ],
      })
    },

    removeTag(index) {
      this.changeSurveyAdvancedOptions({
        advancedKey: this.option,
        value: this.tags.filter((_, tagIndex) => tagIndex !== index),
      })
    },

    handleGroupsToShowChange({ target }, index) {
      const wantedValue = target.value

      const groupsInsideTag = this.tags[index].groups.length || 1

      const canUpdate = groupsInsideTag >= wantedValue

      this.changeSurveyAdvancedOptions({
        advancedKey: this.option,
        value: this.tags.map((tag, tagIndex) =>
          tagIndex === index && canUpdate
            ? { ...tag, maxGroupsToShow: Number(target.value) }
            : tag,
        ),
      })
    },

    handleTagNameChange({ target }, index) {
      this.changeSurveyAdvancedOptions({
        advancedKey: this.option,
        value: this.tags.map((tag, tagIndex) =>
          tagIndex === index ? { ...tag, name: target.value } : tag,
        ),
      })
    },

    addGroup(groupId, index, event) {
      event.stopPropagation()

      this.changeSurveyAdvancedOptions({
        advancedKey: this.option,
        value: this.tags.map((tag, tagIndex) =>
          tagIndex === index ? { ...tag, groups: [...tag.groups, groupId] } : tag,
        ),
      })

      this.closeGroupDrawer()
    },

    removeGroup(groupId, index) {
      this.changeSurveyAdvancedOptions({
        advancedKey: this.option,
        value: this.tags.map((tag, tagIndex) =>
          tagIndex === index
            ? { ...tag, groups: tag.groups.filter(id => id !== groupId) }
            : tag,
        ),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  margin-top: 12px;
  text-align: start;
  padding-left: 12px;
}

.tag-container {
  & + & {
    margin-top: 16px;
  }
}

.tag-header {
  display: flex;
  align-items: center;
}

.space-hz {
  margin-left: 8px;
}

.tag-name-input {
  border: none;

  /* SCSS has a min function. Since css is case-insensitive, we put the css func in caps */
  max-width: MIN(var(--max-length), 100%);
  min-width: 4ch;

  &:hover {
    border: 1px solid hsl(0defg, 20%, 20%);
    border-radius: 4px;
  }

  &.error {
    color: red;
  }
}

.group-options {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: #fff;
  padding: 4px 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1;

  transform: scale(0);
  transform-origin: top left;
  will-change: transform;
  animation: growMenu 0.4s ease forwards;

  > button {
    @include transparentButton;
    justify-content: flex-start;
    font-size: rem(12);
    min-width: 120px;
    max-width: 80vw;
    white-space: nowrap;
    padding: 4px 8px;
    transition: background-color 0.25s ease;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}

.plus {
  padding: 8px;
  @include flexMiddle;
  display: inline-flex;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: rem(18);
  line-height: 1;
  color: white;
  background: $blue;
}

.remove-btn {
  @include transparentButton;
  display: inline-flex;
  margin-right: 12px;
  color: hsl(0defg, 20%, 20%);

  svg {
    @include boxSizing(12);
  }
}

.add-group {
  border: none;
  outline: none;
  background: none;
  border-radius: 50%;
  position: relative;
}

.numeric-tag-wrapper {
  margin-left: 16px;

  > span {
    font-size: rem(14);
  }

  .tag-input {
    font-size: rem(14);

    margin: 0 8px;
    border-radius: 4px;
    border: 1px solid hsl(0defg, 20%, 20%);
    text-align: center;
  }
}

.tag-groups {
  padding-left: 24px;
  font-size: rem(12);

  svg {
    @include boxSizing(10);
  }

  .single-group-line {
    display: flex;
    align-items: center;
    margin: 8px 0;
    color: $gray-2;
  }
}

.add-container {
  margin-top: 12px;

  > button {
    @include transparentButton;

    > span {
      margin-left: 8px;
    }
  }
}

.group-add-background {
  position: fixed;
  background: transparent;
  inset: 0;
}

@keyframes growMenu {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}
</style>
